import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertMessage } from '@/components/ui/AlertMessage';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { adminAPI } from '@/services/api';
import { User } from '@/types';
import { useAuth } from '@/contexts/AuthContext';
import { useNotification, useDebounce } from '@/hooks/common';
import {
  useUserManagementActions,
  useUserManagementInvite,
  useUserManagementState,
  useUserManagementBlockedLogs,
  useUserManagementTabs,
} from '@/hooks/userManagement';
import { UserManagementProvider, useUserManagementContext } from '@/contexts/userManagement';
import { UserManagementHeader } from '@/components/users/management/common/UserManagementHeader';
import {
  PendingUsersTab,
  CustomerUsersTab,
  AllUsersTab,
  BlockedListTab,
  UserSettingsTab,
} from '@/components/users/management/admin/tabs';
import { UserManagementDialogs } from './components';
import { getRoleConfig, UserRole } from '@/utils/roleConfig';
import { USER_MANAGEMENT_TABS, type UserManagementTab } from '@/constants/users';
import { SUBMENU_VALUES } from '@/constants/routing';
import { NOTIFICATION } from '@/constants';

/**
 * 기업 관리자 화면 (Company Admin View)
 * 
 * 페이지 위치: frontend/src/components/users/management/admin/index.tsx
 * 
 * 역할:
 * - 이 화면은 기업 관리자(admin 역할)가 자신의 고객사 내 사용자를 관리하는 화면입니다.
 * - 슈퍼관리자(saas_admin 역할)도 이 화면에 접근할 수 있지만, 접근 시에는 기업 관리자 모드로 동작합니다.
 * - 즉, 슈퍼관리자라도 이 화면에서는 자신의 고객사 정보만 볼 수 있고, 다른 고객사 정보는 볼 수 없습니다.
 * 
 * 보안 원칙:
 * - 이 화면의 모든 컴포넌트는 isSaasAdmin={false}로 전달해야 합니다.
 * - 다른 고객사 정보를 표시하는 필터나 컬럼은 절대 표시되지 않아야 합니다.
 * - customers prop은 항상 빈 배열 []로 전달해야 합니다.
 * 
 * 슈퍼관리자 전용 기능이 필요한 경우:
 * - superadmin/index.tsx (슈퍼관리자 전용 화면)를 사용하세요.
 * 
 * 용어 참고: docs/2.architecture/7.TERMINOLOGY.md
 */
const UserManagementInner: React.FC = () => {
  const { user: currentUser } = useAuth();
  const userRole = (currentUser?.role || 'user') as UserRole;
  const roleConfig = getRoleConfig(userRole);
  // 주의: 이 화면은 기업 관리자 화면이므로, 슈퍼관리자라도 기업 관리자 모드로 동작합니다.
  // 실제 슈퍼관리자 권한이 필요한 경우 superadmin/index.tsx를 사용하세요.
  // isSaasAdmin 변수는 참고용이며, 컴포넌트에 전달할 때는 항상 false로 전달합니다.
  const isSaasAdmin = userRole === 'saas_admin'; // 역할 체크 (참고용)
  const isAdmin = userRole === 'admin';
  const [searchParams, setSearchParams] = useSearchParams();
  const subtabFromUrl = searchParams.get('subtab') as UserManagementTab | null;
  const customerIdFromUrl = searchParams.get('customerId');
  const submenu = searchParams.get('submenu');
  
  // "내 이용자 관리"인지 "전체 이용자 관리"인지 확인
  const isMyUserManagement = submenu === SUBMENU_VALUES.USER_MANAGEMENT;
  const isAllUserManagement = submenu === SUBMENU_VALUES.USER_MANAGEMENT_ALL;
  
  // 고객사 ID 설정:
  // - 기업관리자 또는 "내 이용자 관리": 자신의 고객사 ID 사용
  // - "전체 이용자 관리": null (전체 조회)
  const getInitialCustomerId = (): number | null => {
    if (isAllUserManagement) {
      // "전체 이용자 관리": null (전체 조회)
      return null;
    }
    // 기업관리자 또는 "내 이용자 관리": 자신의 고객사 ID 사용
    if (customerIdFromUrl) {
      return parseInt(customerIdFromUrl, 10);
    }
    return currentUser?.customer?.id || null;
  };
  
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(
    getInitialCustomerId()
  );
  
  // "전체 이용자 관리"는 기본 탭을 'pending'으로 설정 (승인대기중)
  // 기업관리자, "내 이용자 관리"는 기본 탭을 'all'로 설정 (전체사용자)
  const defaultTab: UserManagementTab = isAllUserManagement ? USER_MANAGEMENT_TABS.PENDING : ((isAdmin || isMyUserManagement) ? USER_MANAGEMENT_TABS.ALL : USER_MANAGEMENT_TABS.PENDING);
  
  // URL에서 직접 읽어서 항상 최신 값 사용 (상태 동기화 문제 방지)
  const activeTab: UserManagementTab = subtabFromUrl || defaultTab;
  
  // URL 파라미터 초기화 (한 번만)
  const hasInitializedTab = useRef(false);
  useEffect(() => {
    if (!hasInitializedTab.current && !subtabFromUrl) {
      hasInitializedTab.current = true;
      // "전체 이용자 관리"는 'pending', 기타는 'all'이 기본
      const defaultSubtab: UserManagementTab = isAllUserManagement ? USER_MANAGEMENT_TABS.PENDING : ((isAdmin || isMyUserManagement) ? USER_MANAGEMENT_TABS.ALL : USER_MANAGEMENT_TABS.PENDING);
      // 기업관리자는 자기 고객사만 볼 수 있으므로 customerId를 URL에 노출하지 않음
      setSearchParams({ subtab: defaultSubtab }, { replace: true });
    }
  }, [subtabFromUrl, isAdmin, isMyUserManagement, isAllUserManagement, defaultTab, setSearchParams]);
  
  // customerId가 변경되지 않도록 고정
  useEffect(() => {
    if (isAllUserManagement) {
      // "전체 이용자 관리": null로 고정
      if (selectedCustomerId !== null) {
        setSelectedCustomerId(null);
      }
    } else if (isAdmin || isMyUserManagement) {
      // 기업관리자 또는 "내 이용자 관리": 자신의 고객사 ID로 고정
      const expectedCustomerId = currentUser?.customer?.id || null;
      if (selectedCustomerId !== expectedCustomerId) {
        setSelectedCustomerId(expectedCustomerId);
      }
    }
  }, [isAdmin, isMyUserManagement, isAllUserManagement, currentUser?.customer?.id, selectedCustomerId, searchParams]);

  const handleSubTabChange = (tab: string) => {
    // "전체 이용자 관리"일 때는 'all' 탭만 허용
    if (isAllUserManagement && tab !== USER_MANAGEMENT_TABS.ALL) {
      return; // 다른 탭으로 변경 시도 시 무시
    }

    const validTab = tab as UserManagementTab;
    // 기업관리자는 자기 고객사만 볼 수 있으므로 customerId를 URL에 노출하지 않음
    setSearchParams({ subtab: validTab }, { replace: true });
  };

  const { error, success, setError, setSuccess } = useNotification({ duration: NOTIFICATION.LONG_DURATION });
  const [loading, setLoading] = useState(false);
  
  // Context에서 공통 상태 가져오기
  const {
    editingUser,
    setEditingUser,
    originalUser,
    setOriginalUser,
    passwordChangeUser,
    setPasswordChangeUser,
    authTypeChangeUser,
    setAuthTypeChangeUser,
    alertOpen,
    setAlertOpen,
    alertMessage,
    setAlertMessage,
    alertTitle,
    setAlertTitle,
    showAlert,
  } = useUserManagementContext();
  
  // 나머지 상태 관리 hook (그룹화된 구조)
  const { search, setSearch, blocked, setBlocked, counts, setCounts, refresh, customers } = useUserManagementState();

  // 디바운스된 검색어 (AllUsersTab용)
  const debouncedAllUsersSearchQuery = useDebounce(search.allUsers, 500);
  
  // getCustomerId를 useCallback으로 메모이제이션하여 무한 루프 방지
  const getCustomerId = React.useCallback(() => {
    return isAllUserManagement ? null : (currentUser?.customer?.id || selectedCustomerId);
  }, [isAllUserManagement, currentUser?.customer?.id, selectedCustomerId]);

  // 차단목록 로직 hook (그룹화된 구조 사용)
  const { loadBlockedLogs, handleClearBlockedLogsClick, handleClearBlockedLogs } = useUserManagementBlockedLogs({
    blocked,
    setBlocked,
    showAlert,
    getCustomerId,
    isAllUserManagement,
  });

  // Hooks 사용 (그룹화된 구조)
  const {
    confirmDialogs,
    setConfirmDialogs,
    emailResult,
    setEmailResult,
    approvingUserId,
    handlers,
  } = useUserManagementActions({
    setError,
    setSuccess,
    setLoading,
    refreshAllTabs: () => refreshAllTabsRef.current(),
  });

  const {
    showInviteForm,
    setShowInviteForm,
    inviteLoading,
    handleInviteUsers,
  } = useUserManagementInvite({
    selectedCustomerId,
    currentUserCustomerId: currentUser?.customer?.id,
    setError,
    setSuccess,
    refreshAllTabs: () => refreshAllTabsRef.current(),
  });

  const handleCreateCustomerFromUser = async (user: User) => {
    setAlertTitle('알림');
    setAlertMessage(`사용자 ${user.email}를 위한 고객사를 생성합니다.`);
    setAlertOpen(true);
  };

  const handleUpdateUser = async (user: User, updates?: { auth_type?: string; new_password?: string }) => {
    if (!originalUser) return;
    await handlers.update(user, originalUser, updates);
    setEditingUser(null);
    setOriginalUser(null);
  };

  // 고객사 목록 불러오기 (SaaS 관리자용)
  useEffect(() => {
    if (isSaasAdmin) {
      const fetchCustomers = async () => {
        try {
          const response = await adminAPI.getCustomers();
          customers.setList(response.customers || []);
        } catch (error) {
          console.error('고객사 목록 불러오기 실패:', error);
        }
      };
      fetchCustomers();
    }
  }, [isSaasAdmin, customers.setList]);

  // 차단목록 탭이 선택되거나 includeDeleted가 변경될 때 데이터 로드
  // loadBlockedLogs는 useCallback으로 메모이제이션되어 있지만,
  // includeDeleted가 변경될 때마다 재생성되므로 의존성 배열에서 제거
  useEffect(() => {
    if (activeTab === USER_MANAGEMENT_TABS.BLOCKED) {
      loadBlockedLogs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, blocked.includeDeleted]); // loadBlockedLogs는 제외하여 무한 루프 방지

  // 탭 카운트 및 새로고침 로직 hook (그룹화된 구조 사용)
  const { refreshAllTabsRef } = useUserManagementTabs({
    setCounts,
    triggerRefresh: refresh.trigger,
    loadBlockedLogs,
    isMyUserManagement,
    isAdmin,
    isAllUserManagement,
    selectedCustomerId,
  });


  return (
    <div className="space-y-6">
      <UserManagementHeader
        loading={loading}
        onInviteClick={() => setShowInviteForm(true)}
        onRefresh={() => refreshAllTabsRef.current()}
      />

      {error && (
        <AlertMessage 
          type="error" 
          message={error} 
          onClose={() => setError('')} 
        />
      )}

      {success && (
        <AlertMessage 
          type="success" 
          message={success} 
          onClose={() => setSuccess('')} 
        />
      )}

      <Tabs value={activeTab} onValueChange={handleSubTabChange}>
        <TabsList
          className={`grid w-full ${
            (() => {
              // "내 이용자 관리": 전체사용자 / 승인대기중 / 차단목록 / 이용자설정 (4개)
              if (isMyUserManagement || isAdmin) {
                return 'grid-cols-4';
              }
              // "전체 이용자 관리": 전체사용자 / 승인대기중 (2개)
              if (isAllUserManagement) {
                return 'grid-cols-2';
              }
              // 기본값 (고객사별 사용자 탭 제거됨)
              const baseCount =
                (roleConfig.userManagement.showPendingTab ? 1 : 0) +
                (roleConfig.userManagement.showAllUsersTab ? 1 : 0);
              const extraTabs = isAdmin ? 2 : 0;
              const tabCount = baseCount + extraTabs;
              if (tabCount === 1) return 'grid-cols-1';
              if (tabCount === 2) return 'grid-cols-2';
              if (tabCount === 3) return 'grid-cols-3';
              if (tabCount === 4) return 'grid-cols-4';
              return 'grid-cols-2';
            })()
          }`}
        >
          {/* 승인대기중 탭을 가장 앞에 배치 (기본 탭) */}
          {roleConfig.userManagement.showPendingTab && (
            <TabsTrigger value="pending">
              {`승인대기중 (${counts.pending})`}
            </TabsTrigger>
          )}
          {/* 전체사용자 탭 */}
          {roleConfig.userManagement.showAllUsersTab && (
            <TabsTrigger value={USER_MANAGEMENT_TABS.ALL}>
              {`${roleConfig.userManagement.allUsersTabLabel} (${counts.allUsers})`}
            </TabsTrigger>
          )}
          {/* 고객사별 사용자 탭 제거됨 */}
          {/* 차단목록 / 이용자설정: "내 이용자 관리" 또는 기업관리자만 표시 */}
          {(isMyUserManagement || isAdmin) && (
            <TabsTrigger value={USER_MANAGEMENT_TABS.BLOCKED}>
              {`차단목록 (${counts.blocked})`}
            </TabsTrigger>
          )}
          {(isMyUserManagement || isAdmin) && (
            <TabsTrigger value={USER_MANAGEMENT_TABS.SETTINGS}>이용자설정</TabsTrigger>
          )}
        </TabsList>

        <TabsContent value={USER_MANAGEMENT_TABS.PENDING} className="space-y-4">
          <PendingUsersTab
            key={refresh.key}
            fixedCustomerId={isAllUserManagement ? null : selectedCustomerId}
            showCustomerFilter={false}
            useBackendFiltering={false}
            onEditUser={(user) => {
              setEditingUser({ ...user });
              setOriginalUser({ ...user });
            }}
            onApproveUser={handlers.approve}
            onCreateCustomer={handleCreateCustomerFromUser}
            showCreateCustomer={isSaasAdmin && isAllUserManagement}
            onRefresh={() => refreshAllTabsRef.current()}
          />
        </TabsContent>

        {/* 고객사별 사용자 탭 제거됨 */}

        {roleConfig.userManagement.showAllUsersTab && (
          <TabsContent value={USER_MANAGEMENT_TABS.ALL} className="space-y-4">
            {isAdmin || isMyUserManagement ? (
              // 기업관리자 또는 슈퍼관리자의 "내 이용자 관리": 자신의 기업 내 전체 사용자 (customerId 자동 설정)
              <CustomerUsersTab
                key={refresh.key}
                customerId={selectedCustomerId}
                searchQuery={search.allUsers}
                debouncedSearchQuery={debouncedAllUsersSearchQuery}
                onSearchChange={(query) => setSearch({ allUsers: query })}
                // 기업관리자와 슈퍼관리자의 "내 이용자 관리"는 고객사 선택/뒤로가기 기능이 필요 없으므로 no-op 핸들러 전달
                onSelectCustomer={() => {}}
                onBack={() => {}}
                hideCustomerSelection={true}
                onEditUser={(user) => {
                  setEditingUser({ ...user });
                  setOriginalUser({ ...user });
                }}
                onPasswordChange={setPasswordChangeUser}
                onAuthTypeChange={setAuthTypeChangeUser}
                onApproveUser={handlers.approve}
                onDeactivateUser={handlers.deactivate.click}
                onActivateUser={handlers.activate.click}
                onDeleteUser={handlers.delete.click}
                approvingUserId={approvingUserId}
              />
            ) : (
              // 슈퍼관리자의 "전체 이용자 관리": 전체 사용자
              <AllUsersTab
                key={refresh.key}
                fixedCustomerId={currentUser?.customer?.id || null}
                showCustomerFilter={false}
                showCustomerColumn={false}
                onEditUser={(user) => {
                  setEditingUser({ ...user });
                  setOriginalUser({ ...user });
                }}
                onDeactivateUser={handlers.deactivate.click}
                onActivateUser={handlers.activate.click}
                onDeleteUser={handlers.delete.click}
                refreshKey={refresh.key}
              />
            )}
          </TabsContent>
        )}

        {(isMyUserManagement || isAdmin) && (
          <TabsContent value={USER_MANAGEMENT_TABS.BLOCKED} className="space-y-4">
            <BlockedListTab
              logs={blocked.logs}
              loading={blocked.loading}
              onRefresh={() => refreshAllTabsRef.current()}
              onClear={handleClearBlockedLogsClick}
              customerId={selectedCustomerId || currentUser?.customer?.id || null}
              includeDeleted={blocked.includeDeleted}
              onIncludeDeletedChange={(include) => {
                setBlocked({ includeDeleted: include });
              }}
              isSuperAdmin={false}
              useUrlSync={false}
            />
          </TabsContent>
        )}

        {(isMyUserManagement || isAdmin) && (
          <TabsContent value={USER_MANAGEMENT_TABS.SETTINGS} className="space-y-4">
            <UserSettingsTab customer={currentUser?.customer} />
          </TabsContent>
        )}
      </Tabs>

      <UserManagementDialogs
        editingUser={editingUser}
        originalUser={originalUser}
        loading={loading}
        onEditUserClose={() => {
          setEditingUser(null);
          setOriginalUser(null);
        }}
        onSaveUser={handleUpdateUser}
        passwordChangeUser={passwordChangeUser}
        onPasswordChangeClose={() => setPasswordChangeUser(null)}
        onPasswordSubmit={handlers.password}
        authTypeChangeUser={authTypeChangeUser}
        onAuthTypeChangeClose={() => setAuthTypeChangeUser(null)}
        onAuthTypeSubmit={handlers.authType}
        deactivateConfirmOpen={confirmDialogs.deactivate.open}
        userToDeactivate={confirmDialogs.deactivate.target}
        onDeactivateConfirm={handlers.deactivate.confirm}
        onDeactivateCancel={() => {
          setConfirmDialogs({ deactivate: { target: null, open: false } });
        }}
        activateConfirmOpen={confirmDialogs.activate.open}
        userToActivate={confirmDialogs.activate.target}
        onActivateConfirm={handlers.activate.confirm}
        onActivateCancel={() => {
          setConfirmDialogs({ activate: { target: null, open: false } });
        }}
        deleteConfirmOpen={confirmDialogs.delete.open}
        userToDelete={confirmDialogs.delete.target}
        onDeleteConfirm={handlers.delete.confirm}
        onDeleteCancel={() => {
          setConfirmDialogs({ delete: { target: null, open: false } });
        }}
        showInviteForm={showInviteForm}
        inviteLoading={inviteLoading}
        customers={customers.list}
        showCustomerSelect={isSaasAdmin && activeTab === USER_MANAGEMENT_TABS.ALL}
        onInviteClose={() => setShowInviteForm(false)}
        onInviteSubmit={handleInviteUsers}
        emailResultDialogOpen={emailResult.dialogOpen}
        emailResult={emailResult.data}
        onEmailResultClose={() => setEmailResult({ dialogOpen: false })}
        clearBlockedConfirmOpen={blocked.confirmOpen}
        onClearBlockedConfirm={handleClearBlockedLogs}
        onClearBlockedCancel={() => setBlocked({ confirmOpen: false })}
        alertOpen={alertOpen}
        alertTitle={alertTitle}
        alertMessage={alertMessage}
        onAlertClose={() => setAlertOpen(false)}
      />
    </div>
  );
};

const UserManagement: React.FC = () => {
  return (
    <UserManagementProvider>
      <UserManagementInner />
    </UserManagementProvider>
  );
};

export default UserManagement;
